import {useMemo} from 'react';
import {Concept} from 'types';
import {useAllThesaurusMap} from 'services/thesaurus';
import {useModifiedSchema} from 'schemas/hooks/useModifiedSchema';
import type {ActualChangesWithSchema} from '../types';
import {getActualChangesWithSchema} from '../functions/getActualChangesWithSchema';
import {useAggregatedStatus} from './useAggregatedStatus';
import {useAssertChangeRequest} from './useAssertChangeRequest';
import {useAssertMetadataEdit} from './useAssertMetadataEdit';
import {useChangeRequestImportProps} from './useChangeRequestImportProps';

export const useActualChangesWithSchema = (
  changeRequestId: string,
): ActualChangesWithSchema | undefined => {
  const metadata = useAssertMetadataEdit();
  const thesaurusMap = useAllThesaurusMap();

  const changeRequest = useAssertChangeRequest(changeRequestId);

  const relatedProps = useChangeRequestImportProps(
    changeRequest.manifestation.id,
    changeRequest.expression.id,
  );

  const workStatus = useAggregatedStatus(changeRequest.work.id);
  const expressionStatus = useAggregatedStatus(changeRequest.expression.id);
  const manifestationStatus = useAggregatedStatus(
    changeRequest.manifestation.id,
  );

  const modifiedManifestationSchema = useModifiedSchema(
    changeRequest.manifestation,
    relatedProps.work.type,
    Concept.manifestation,
    manifestationStatus,
  );

  const modifiedExpressionSchema = useModifiedSchema(
    changeRequest.expression,
    relatedProps.work.type,
    Concept.expression,
    expressionStatus,
  );

  const modifiedWorkSchema = useModifiedSchema(
    changeRequest.work,
    relatedProps.work.type,
    Concept.work,
    workStatus,
  );

  return useMemo(
    () =>
      getActualChangesWithSchema(
        metadata,
        changeRequest,
        {
          work: modifiedWorkSchema,
          expression: modifiedExpressionSchema,
          manifestation: modifiedManifestationSchema,
        },
        thesaurusMap,
      ),
    [
      changeRequest,
      metadata,
      modifiedExpressionSchema,
      modifiedManifestationSchema,
      modifiedWorkSchema,
      thesaurusMap,
    ],
  );
};
